import _ from "lodash";
import submit from "submit-form";

import { PARAMETER_OPTIONS } from "../constants";
const { LOAD, SETS, RPEAMRAP, REPS, ROUNDS } = PARAMETER_OPTIONS;

import { mapParameter } from "../parts/other-exercise/CustomParametersSets";

export function prevalidateSet(set, isAssistant) {
  return set.rpe === "AMRAP"
    ? Boolean(set.sets)
    : Boolean(set.sets && set.reps);
}

export function validateSet({ locals, exercise, set, defaultRepMax, field }) {
  const { activeUser, assert, flash } = locals;
  try {
    const primary = _.get(exercise, "primary");

    if (set.reps) set.reps = String(set.reps);

    let repMax = 0;

    if (exercise.parameters) {
      const isRepRange = set.reps && set.reps.includes("-");
      if (isRepRange) {
        assert(
          set.reps.match(/^\d{1,4}-?(\d{1,4})?$/),
          400,
          "Reps must be a valid range (e.g. 4-6)"
        );
        const [repsLower, reps] = set.reps.split("-");
        set.reps = reps || repsLower;
        if (reps) {
          set.repsLower = repsLower;
        }
      }
      if (!isRepRange && set.repsLower && field === "reps")
        set.repsLower = null;
      return set;
    }
    if (exercise.type != "secondary") {
      if (set.equipped && set.equippedRepMax) {
        repMax = Number(set.equippedRepMax);
      } else {
        repMax =
          defaultRepMax ||
          _.get(
            exercise,
            `repMaximums.${primary}`,
            _.get(activeUser, `repMaximums.${primary}`, 0)
          );
        set.equippedRepMax = repMax; // defaulting equippedRepMax to defaultRepMax (1RM)
      }
    } else {
      repMax = exercise.secondaryRepMaximum;

      // There were a bug after duplicating the exercises of a cycle, this handle it
      if (repMax === 0) {
        repMax = _.get(exercise, `repMaximums.${exercise.primary}`);
      }
    }

    delete set.equipment;
    delete set.equippedRepMax;

    const isAssistant = _.get(exercise, "type") === "assistant";
    const isPrimary = _.get(exercise, "type") === "primary";
    assert(
      isPrimary || exercise.secondary,
      400,
      `You must select ${isAssistant ? "an assistant" : "a secondary"} movement`
    );
    assert(
      isAssistant || repMax > 0,
      400,
      "Rep Maximum must be greater than 0 to add a set"
    );

    // Below commented line is not needed; set.load, equippedRepMax and defaultRepMax are always in KG
    // if (set.loadType === "%" || set.percentRepMax && !set.equipped) set.load = convert(repMax * (Number(set.percentRepMax) / 100)).from("kg").to(activeUser.units === "imperial" ? "lb" : "kg");
    delete set.loadType;
    delete set.loadTypeEdit;

    assert(set.sets > 0, 400, "Sets must be greater than 0");

    const isRepRange = set.reps && set.reps.includes("-");
    if (isRepRange) {
      assert(
        set.reps.match(/^\d{1,4}-?(\d{1,4})?$/),
        400,
        "Reps must be a valid range (e.g. 4-6)"
      );
      const [repsLower, reps] = set.reps.split("-");
      set.reps = reps || repsLower;
      if (reps) {
        set.repsLower = repsLower;
      }
    }

    if (set.rpe !== "AMRAP") {
      if (isRepRange && set.repsLower) {
        assert(set.repsLower > 0, 400, "Reps must be greater than 0");
      } else {
        assert(set.reps.match(/^\d{1,4}$/), 400, "Reps must be an integer");
        assert(set.reps > 0, 400, "Reps must be greater than 0");
      }
    }
    if (!isRepRange && set.repsLower && field === "reps") set.repsLower = null;
    assert(set.sets <= 99, 400, "An exercise cannot have that many sets");

    if (!isAssistant) {
      if (set.percentRepMax) {
        assert(
          /^\d+([.,]\d*)?$/.test(set.percentRepMax),
          400,
          "%1RM must be a valid number"
        );
      }
      if (set.load)
        assert(!isNaN(set.load), 400, "Load must be a valid number");
    }
    if (process.browser) {
      if (!isAssistant) {
        // no conversion needed both set.load and repMax are alwasy in KG see handleNew/EditSetChange
        if (
          _.round(set.load, 2) > _.round(repMax, 2) &&
          !confirm("Are you sure you want to program above 100% of 1RM?")
        ) {
          return undefined;
        }
      }
      return set;
    }
  } catch (err) {
    flash("error", err.message);
    submit("/actions/refresh");
  }
}

export function validateAssistantSet({ locals, set }) {
  const { assert, flash } = locals;
  try {
    if (set.reps) set.reps = String(set.reps);
    const isRepRange = set.reps && set.reps.includes("-");
    if (isRepRange) {
      assert(
        set.reps.match(/^\d{1,4}-?(\d{1,4})?$/),
        400,
        "Reps must be a valid range (e.g. 4-6)"
      );
      const [repsLower, reps] = set.reps.split("-");
      set.reps = reps || repsLower;
      if (reps) {
        set.repsLower = repsLower;
      }
    }

    if (set.rpe !== "AMRAP") {
      if (isRepRange && set.repsLower) {
        assert(set.repsLower > 0, 400, "Reps must be greater than 0");
      } else {
        assert(set.reps.match(/^\d{1,4}$/), 400, "Reps must be an integer");
        assert(set.reps > 0, 400, "Reps must be greater than 0");
      }
    }
    if (!isRepRange && set.repsLower) set.repsLower = null;

    return set;
  } catch (err) {
    flash("error", err.message);
    submit("/actions/refresh");
  }
}

export function validateExercise(exercise, locals) {
  const { activeUser, assert, flash } = locals;
  try {
    // ensuring load follows repMax
    let repMax = 0;
    if (exercise.type != "secondary") {
      repMax =
        exercise.repMaximums[exercise.primary] ||
        _.get(activeUser, `repMaximums.${exercise.primary}`, 0);
    } else {
      repMax = exercise.secondaryRepMaximum;
    }
    // Ensure secondary/assistant exercises specify a movement
    const isPrimary = _.get(exercise, "type") === "primary";
    assert(
      isPrimary || exercise.secondary,
      400,
      `You must select ${
        exercise.type === "assistant" ? "an assistant" : "a secondary"
      } movement.`
    );

    exercise.sets = _.map(exercise.sets, (set) => {
      delete set.editMode;
      delete set.units;
      delete set.idx;
      // ensures load follows intensity
      set.load = set.percentRepMax
        ? _.round((set.percentRepMax * repMax) / 100, 2)
        : _.round(set.load, 2);
      if (isNaN(set.load)) set.load = 0;
      return set;
    });
    if (exercise.type === "primary") delete exercise.secondary;
    return exercise;
  } catch (err) {
    flash("error", err.message);
    submit("/actions/refresh");
    return false;
  }
}

export function prevalidateOtherSet({ parameters }, parametersForm) {
  let valid = true;
  if (detectMuscle(parameters)) {
    parameters.forEach((value) => {
      if (value === SETS && parametersForm[mapParameter(value)] === "")
        valid = false;

      if (
        value === REPS &&
        parametersForm[mapParameter(value)] === "" &&
        parametersForm.rpe !== "AMRAP"
      )
        valid = false;
    });
  } else if (detectNoSetsRounds(parameters)) {
    let countInvalid = 0;
    parameters.forEach((value) => {
      if (parametersForm[mapParameter(value)] === "") countInvalid++;
    });

    if (countInvalid === parameters.length) valid = false;
  } else {
    parameters.forEach((value) => {
      if (
        [SETS, ROUNDS].includes(value) &&
        parametersForm[mapParameter(value)] === ""
      )
        valid = false;
    });
  }

  return valid;
}

const detectMuscle = (parameters) =>
  parameters.join() === [SETS, REPS, RPEAMRAP, LOAD].join();

const detectNoSetsRounds = (parameters) => {
  let noExist = true;

  parameters.forEach((elem) => {
    if (elem === SETS || elem === ROUNDS) noExist = false;
  });

  return noExist;
};

export function checkOtherExercise(exercises) {
  let valid = true;

  const alertFalse = (param, name) => {
    alert(`${param} field cannot be blank. (${name})`);
    valid = false;
  };

  exercises.forEach((exercise) => {
    if (
      exercise.parameters &&
      (exercise.parameters.includes(ROUNDS) ||
        exercise.parameters.includes(SETS))
    ) {
      exercise.sets.forEach((set) => {
        if (exercise.parameters.includes(ROUNDS) && !set.rounds) {
          alertFalse(ROUNDS, exercise.secondary);
        }
        if (exercise.parameters.includes(SETS) && !set.sets) {
          alertFalse(SETS, exercise.secondary);
        }
      });
    }
  });
  return valid;
}

/**
 * Determine if an exercise can be swapped for another exercise
 * @param  {Object} originalExercise Exercise to be swapped from
 * @param  {Object} swapExercise Exercise to be swapped to
 * @return {Boolean} Whether originalExercise can be swapped to swapExercise
 */
export function canSwap(originalExercise = {}, swapExercise = {}) {
  const { parameters: originalParams } = originalExercise ?? {};
  const { parameters: swapParams } = swapExercise ?? {};
  const MUSCLE = [SETS, REPS, RPEAMRAP, LOAD];

  // allows swapping an exercise without parameters to an exercise that has sets, reps, rpe, load as parameters
  // also can swap exercises with the same parameters
  return !!(
    (!originalParams && swapParams && _.isEqual(MUSCLE, swapParams)) ||
    (originalParams && !swapParams && _.isEqual(MUSCLE, originalParams)) ||
    (originalParams && swapParams && _.isEqual(originalParams, swapParams)) ||
    (!originalParams && !swapParams)
  );
}
